import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch, readJsonResponse } from '../api/client';
import { withScheduleGaps } from './chartScheduleGaps';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Вся жизнь' },
  { value: '1y', label: '1 год' },
  { value: '90d', label: '90 дней' },
  { value: '30d', label: '30 дней' },
];

function formatDateTime(value) {
  if (!value) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatAxisDate(value, period) {
  if (!value) {
    return '';
  }
  const options =
    period === '30d'
      ? { day: '2-digit', month: '2-digit' }
      : { month: 'short', year: '2-digit' };
  return new Intl.DateTimeFormat('ru-RU', options).format(new Date(value));
}

/** Axis ticks stay in one unit so labels never wrap or switch mid-scale. */
export function formatAxisSession(milliseconds) {
  const seconds = Number(milliseconds) / 1000;
  if (!Number.isFinite(seconds)) {
    return '';
  }
  return `${Math.round(seconds)} с`;
}

/** Exact milliseconds stay visible; longer runs also read as seconds. */
export function formatSession(milliseconds) {
  const value = Number(milliseconds);
  if (!Number.isFinite(value)) {
    return '—';
  }
  if (value < 10000) {
    return `${value} мс`;
  }
  const seconds = value / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)} с`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest ? `${minutes} мин ${rest} с` : `${minutes} мин`;
}

function SessionTooltip({ active, payload }) {
  if (!active || !payload?.[0]?.payload) {
    return null;
  }
  const point = payload[0].payload;
  if (point.is_gap) {
    return null;
  }
  return (
    <div className="max-w-[16rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-xl dark:border-slate-700 dark:bg-slate-950">
      <p className="font-semibold text-rose-700 dark:text-rose-300">
        Минимум: {formatSession(point.minimum_prev_esp_ms)} · {point.minimum_count || 0} раз
      </p>
      <p className="text-slate-500">{formatDateTime(point.minimum_at)}</p>
      <p className="mt-1 font-semibold text-sky-700 dark:text-sky-300">
        Максимум: {formatSession(point.maximum_prev_esp_ms)} · {point.maximum_count || 0} раз
      </p>
      <p className="text-slate-500">{formatDateTime(point.maximum_at)}</p>
      <p className="mt-1 text-slate-500">Замеров в интервале: {point.sample_count}</p>
    </div>
  );
}

/** A hole only when the next sample is later than this device's link interval. */
export function buildSessionChartData(points, _bucketSeconds, periodSeconds) {
  return withScheduleGaps(points, periodSeconds, {
    minimum_prev_esp_ms: null,
    maximum_prev_esp_ms: null,
  });
}

/** Lifetime radio-on time. Every bucket keeps its exact minimum and maximum. */
export default function DevicePrevEspHistory({ deviceId, onLoadingChange }) {
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    if (!deviceId) {
      setIsLoading(false);
      onLoadingChangeRef.current?.(false);
      return undefined;
    }

    const abortController = new AbortController();
    async function load() {
      setIsLoading(true);
      onLoadingChangeRef.current?.(true);
      setError(null);
      try {
        const response = await apiFetch(
          `/api/devices/${deviceId}/prev-esp-history?period=${encodeURIComponent(period)}`,
          { signal: abortController.signal },
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || `Ошибка ${response.status}`);
        }
        const payload = await readJsonResponse(response, 'История работы радио');
        if (!abortController.signal.aborted) {
          setData(payload);
        }
      } catch (loadError) {
        if (abortController.signal.aborted || loadError?.name === 'AbortError') {
          return;
        }
        setData(null);
        setError(loadError.message || 'Не удалось загрузить длительность работы радио');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          onLoadingChangeRef.current?.(false);
        }
      }
    }

    load();
    return () => abortController.abort();
  }, [deviceId, period]);

  const points = data?.points || [];
  const chartData = useMemo(
    () => buildSessionChartData(data?.points || [], data?.bucket_seconds, data?.connection_period_seconds),
    [data?.bucket_seconds, data?.connection_period_seconds, data?.points],
  );

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">Радио</p>
          <h2 className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-white">
            Длительность работы радио за жизнь устройства
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            В каждом интервале сохранены и самая короткая, и самая долгая работа радио
          </p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800/80">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                period === option.value
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="mt-3 h-56 animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
      ) : error ? (
        <p className="mt-3 rounded-lg border border-amber-300/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          {error}
        </p>
      ) : points.length === 0 ? (
        <p className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-950/40">
          Устройство ещё не передавало длительность работы радио
        </p>
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Самый короткий</p>
              <p className="text-lg font-semibold tabular-nums text-rose-700 dark:text-rose-300">
                {formatSession(data.lowest_prev_esp_ms)}
              </p>
              <p className="text-[11px] text-slate-500">
                {data.lowest_count} раз · {formatDateTime(data.lowest_at)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Самый длинный</p>
              <p className="text-lg font-semibold tabular-nums text-sky-700 dark:text-sky-300">
                {formatSession(data.highest_prev_esp_ms)}
              </p>
              <p className="text-[11px] text-slate-500">
                {data.highest_count} раз · {formatDateTime(data.highest_at)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Интервал графика</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.bucket_label || '—'}
              </p>
              <p className="text-[11px] text-slate-500">{data.sample_count} замеров, ноль отброшен</p>
            </div>
          </div>

          <div className={`mt-3 h-72 transition-opacity ${isLoading ? 'opacity-55' : 'opacity-100'}`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="bucket_ms"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  minTickGap={32}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => formatAxisDate(value, period)}
                />
                <YAxis
                  type="number"
                  domain={[
                    (minimum) => Math.max(0, Math.floor(minimum * 0.9)),
                    (maximum) => Math.ceil(maximum * 1.05),
                  ]}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => formatAxisSession(value)}
                  width={44}
                />
                <Tooltip content={<SessionTooltip />} />
                <Line
                  type="linear"
                  dataKey="minimum_prev_esp_ms"
                  name="Минимум"
                  stroke="#e11d48"
                  strokeWidth={1.75}
                  connectNulls={false}
                  isAnimationActive={false}
                  dot={{ r: 2.5, fill: '#e11d48', stroke: '#fff', strokeWidth: 1 }}
                />
                <Line
                  type="linear"
                  dataKey="maximum_prev_esp_ms"
                  name="Максимум"
                  stroke="#0284c7"
                  strokeWidth={1.75}
                  connectNulls={false}
                  isAnimationActive={false}
                  dot={{ r: 2.5, fill: '#0284c7', stroke: '#fff', strokeWidth: 1 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            Красная линия — минимум работы радио в интервале, синяя — максимум. Ноль не считается. Дырка — пропуск дольше интервала связи ({data?.connection_period_label || 'день'}).
          </p>
        </>
      )}
    </section>
  );
}
